import Header from 'components/_OldDesign/header';
import React, { ReactElement, useEffect, useState } from 'react';
import style from 'styles/Content.module.scss';
import { GetStaticProps } from 'next';
import 'react-multi-carousel/lib/styles.css';
import {
  be,
  turnstileSiteKey,
} from 'models/conent';
import { useRouter } from 'next/router';
import { findContent, translate } from 'utils/translate';
import axios from 'axios';
import { GTMPageView } from 'utils/gtm';
import OldLayout from '@components/_OldDesign/OldLayout';
import { NextPageWithLayout } from './_app';
import fetchStrapi from 'lib/fetchStrapi';
import { Lang, useLocale } from '@components/Contexts/LocaleContext';
import { PDPAInfoPayload } from '@components/_OldDesign/PDPAConsent/PDPAConsent.model';
import { captureException } from '@sentry/react';
import { initialPDPAInfoPayload, PDPAConsent } from '@components/PDPAConsent';
import ErrorModal from '../components/shared/ErrorModal';
import SuccessRegistration from '@components/eSignature/FormsSection/SuccessRegistration/SuccessRegistration';
import Link from 'next/link';
import Turnstile from 'react-cloudflare-turnstile';
import { useForm } from 'react-hook-form';
import { HoneyPot } from '@components/HoneyPot';
import { formatRules, requiredRule } from 'utils/validation';

export const getStaticProps: GetStaticProps = async () => {
  try {
    const contents = await fetchStrapi('/contact-uses', 2);
    return {
      props: {
        contents,
      },
    };
  } catch (error) {
    // // console.log(error);
    return {
      props: {
        data: JSON.stringify(error),
      },
    };
  }
};

const DEFAULT_MESSAGE_TH = (
  <>
    <div className="text-lg font-semibold text-center text-red-600 mb-2">
      เกิดข้อผิดพลาด
    </div>
    <div className="text-center text-gray-700 mb-4">
      คุณสามารถติดต่อฝ่ายขายโดยตรงได้ที่
      <br />
      <Link href="mailto:sales@codium.co">
        <span className="font-bold text-blue-600">sales@codium.co</span>
      </Link>
      <br />
      เราขออภัยในความไม่สะดวก
    </div>
  </>
);

const DEFAULT_MESSAGE_EN = (
  <>
    <div className="text-lg font-semibold text-center text-red-600 mb-2">
      Oops! Something went wrong.
    </div>
    <div className="text-center text-gray-700 mb-4">
      You can contact our sales team directly at
      <br />
      <Link href="mailto:sales@codium.co">
        <span className="font-bold text-blue-600">sales@codium.co</span>
      </Link>
      <br />
      We apologize for the inconvenience.
    </div>
  </>
);

type FormValue = {
  name: string;
  email: string;
  phone: string;
  company_name: string;
  job: string;
  plans: string;
  none: any;
  user_code: string;
};

const fieldMap = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  company_name: 'company_name',
  job: 'job',
  plans: 'plans',
};

// The registered name, not the CMS name. They coincide for every field the
// forms ship with today, but an editor can add one that `fieldMap` does not
// know — registering those under a shared 'none' key made two such fields
// collide and hid their errors, so a required one killed submit silently.
const fieldNameOf = (name: string) => fieldMap[name] || name || 'none';

const ContactUs: NextPageWithLayout<{ contents: any }> = ({ contents }) => {
  const { lang, setLang } = useLocale();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValue>();
  const [pdpaInfo, setPdpaInfo] = useState<PDPAInfoPayload>(null);
  const [pdpaFormValid, setPdpaFormValid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const [confirmRecaptcha, setConfirmRecaptcha] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const router = useRouter();
  useEffect(() => {
    if (recaptchaToken) {
      setConfirmRecaptcha(true);
    } else {
      setConfirmRecaptcha(false);
    }
  }, [recaptchaToken]);

  useEffect(() => {
    const initLang = (localStorage.getItem('lang') ?? 'th') as Lang;
    setLang(initLang);
  }, [router]);

  useEffect(() => {
    const initPdpaInfo = initialPDPAInfoPayload(null, 'Contact form');
    setPdpaInfo(initPdpaInfo);
  }, []);

  const handleRecaptchaChange = (token: string) => {
    setRecaptchaToken(token);
  };

  const handleSubmitForm = (data: FormValue, e: any) => {
    e.preventDefault();

    if (data.user_code) {
      return;
    }

    if (!recaptchaToken) {
      alert(
        lang === 'en'
          ? 'Please confirm that you are not a robot.'
          : 'กรุณายืนยันว่าคุณไม่ใช่หุ่นยนต์',
      );
      return;
    }

    submit(data, recaptchaToken);
  };

  const submit = async (data: FormValue, recaptchaToken: string) => {
    const url_ads = window.location.href;
    const from_url = localStorage.getItem('fromUrl') ?? url_ads;

    setIsSubmitting(true);

    const dataForm = {
      data: {
        name: data.name,
        email: data.email,
        plans: data.plans,
        job: data.job,
        company_name: data.company_name,
        phone: data.phone,
        from_url,
        recaptcha: recaptchaToken,
      },
      pdpa_info: {
        customer_code: data.email,
        purposes: pdpaInfo.purposes,
      },
    };

    axios
      .post(`${be}/api/contact-forms`, dataForm)
      .then(function ({ data: res }) {
        // Google tag manager for submit
        GTMPageView(url_ads, 'contactFormSubmit');

        setShowSuccess(true);
      })
      .catch((error) => {
        setShowErrorModal(true); // Show error modal on submit error
        captureException(error);
      })
      .finally(() => {
        setIsSubmitting(false);
        reset();
        const initPdpaInfo = initialPDPAInfoPayload(null, 'Codium form');
        setPdpaInfo(initPdpaInfo);
        setRecaptchaToken('');
      });
  };

  return (
    <div className="w-full">
      <ErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
      >
        {lang === 'en' ? DEFAULT_MESSAGE_EN : DEFAULT_MESSAGE_TH}
      </ErrorModal>
      <Header
        content={{
          title:
            'Contact CODIUM - ติดต่อเราเพื่อเริ่มต้นการเปลี่ยนแปลงสู่ดิจิทัล',
          content:
            'ติดต่อ CODIUM วันนี้ เพื่อสอบถามข้อมูลหรือปรึกษาเกี่ยวกับโซลูชันดิจิทัลสำหรับธุรกิจของคุณ เราพร้อมช่วยคุณเปลี่ยนสู่ยุคดิจิทัล',
          image: null,
        }}
      />
      <div
        className={`h-full ${style.bgBlue} ${style.header} w-full relative py-24 pt-80 xl:pt-28  xl:h-screen`}
      >
        <p className="text-white text-4xl text-center font-light mb-8">
          {translate(
            findContent(contents, 'OUR SHUTTLE READY TO LAUNCH'),
            'title',
            lang,
          )}
        </p>
        <p className={`text-4xl text-center font-light ${style.textSkyBlue}`}>
          {translate(
            findContent(contents, 'OUR SHUTTLE READY TO LAUNCH'),
            'description',
            lang,
          )}
        </p>
        <div className="w-48 border-2 bg-red-500  border-red-500 m-auto mt-16"></div>
        {findContent(contents, 'OUR SHUTTLE READY TO LAUNCH')?.subContent.map(
          (item, i) => (
            <h1
              key={i}
              className="text-white text-4xl text-center font-light mt-36"
            >
              {translate(item, 'description', lang)}
            </h1>
          ),
        )}
      </div>

      <div className="mx-12 bg-white relative">
        <div className="grid grid-cols-3 w-fullscreen font-normal text-4xl my-16 xl:mb-0 ">
          <div data-aos="zoom-in-left" data-aos-delay="1000">
            <img
              src="/images/contact/line-left.svg"
              alt="อัพโหลดเอกสารต่างๆเข้าระบบอย่างง่ายดาย"
              className="xl:w-64 md:ml-auto"
              loading="lazy"
            />
          </div>
          <h3
            className={`xl:m-0 text-center w-full xl:mb-0`}
            style={{ position: 'relative' }}
          >
            {findContent(contents, 'CONTACT US')?.title}
          </h3>

          <div data-aos="zoom-in-right" data-aos-delay="1000">
            <img
              src="/images/contact/line-right.svg"
              alt="อัพโหลดเอกสารต่างๆเข้าระบบอย่างง่ายดาย"
              className="xl:w-64"
              loading="lazy"
            />
          </div>
        </div>
      </div>
      <div className="mb-24 grid-flow-col relative xl:hidden">
        <div className="w-24 border-2 bg-red-500  border-red-500 h-0 mr-auto mt-16 absolute  -top-16"></div>
        <h1 className="text-4xl font-normal py-16 px-40 text-center pt-16 xl:text-2xl">
          {translate(findContent(contents, 'CONTACT US'), 'description', lang)}
        </h1>
        <div className="w-24 border-2 bg-red-500  border-red-500 h-0 ml-auto mt-16 absolute right-0 -top-16"></div>
      </div>
      <div className="mb-24 hidden xl:grid grid-cols-4 relative">
        <div className="w-16 border-2 bg-red-500  border-red-500 h-0 ml-auto mt-16 float-right -top-16"></div>
        <h1 className="text-4xl font-normal py-16 text-center  col-span-2 pt-16 xl:text-xl xl:pb-0">
          {translate(findContent(contents, 'CONTACT US'), 'description', lang)}
        </h1>
        <div className="w-16 border-2 bg-red-500  border-red-500 h-0 mr-auto mt-16 right-0"></div>
      </div>

      <form
        className="px-16 pb-24 xl:px-64"
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        {findContent(contents, 'CONTACT US')?.form.map((item, i) => (
          <span key={i}>
            {item.inputType === 'text' ? (
              <div className="mt-1 relative">
                <label className="block text-left text-3xl md:text-2xl lg:text-xl xl:text-sm font-medium text-gray-700">
                  {translate(item, 'label', lang)}
                  {item.required && <span className="text-red">*</span>}
                </label>
                <input
                  type="text"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm text-left border-gray-300 border-b-1 my-8 p-4 pb-1 text-3xl md:text-2xl lg:text-xl xl:text-sm xl:text-xs xl:my-6"
                  name={item.name}
                  placeholder={translate(item, 'placeHolder', lang)}
                  {...register(fieldNameOf(item.name), {
                    ...requiredRule(
                      item.required,
                      translate(item, 'label', lang),
                      lang,
                    ),
                    ...formatRules(item.name, lang),
                  })}
                />
                {errors[fieldNameOf(item.name)] && (
                  <p className="text-red text-left text-3xl md:text-2xl lg:text-xl xl:text-xs -mt-6 mb-6 xl:-mt-4 xl:mb-4">
                    {errors[fieldNameOf(item.name)].message}
                  </p>
                )}
              </div>
            ) : (
              ''
            )}
            {item.inputType === 'files' ? (
              <>
                <label className="block text-left text-4xl font-medium text-gray-700">
                  {translate(item, 'label', lang)}
                </label>
                <div className="mt-1 relative border-dashed border-gray-300  border-2">
                  <input
                    type="text"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-7 pr-12 sm border-gray-300 my-8 p-4 text-center"
                    placeholder="Click to upload"
                  />
                </div>{' '}
              </>
            ) : (
              ''
            )}
            {/* {item.inputType === 'button' ? <div className="flex justify-center py-6">
                    <button type="submit" className="btn custom-blue-button mt-8 md:mt-6 xl:mt-4">
                        {translate(item, 'label', lang)}
                    </button>
                </div> : ''} */}
          </span>
        ))}

        {/* Hidden Honeypot Field */}
        <HoneyPot register={register} onChange={() => {}} />

        <div className="my-12 text-xl xl:text-xs">
          <PDPAConsent
            choices={['PP-1', 'PP-2', 'PP-3']}
            payload={pdpaInfo}
            setPayload={setPdpaInfo}
            valid={pdpaFormValid}
            setValid={setPdpaFormValid}
            newDesign={true}
          />
        </div>

        <div className="w-full text-center">
          <div className="w-fit mx-auto">
            <Turnstile
              turnstileSiteKey={turnstileSiteKey}
              callback={(token) => handleRecaptchaChange(token)}
              theme="light"
            />
          </div>
          <button
            type="submit"
            className="btn custom-blue-button mt-8 md:mt-6 xl:mt-4 disabled:bg-neutral-300"
            disabled={!pdpaFormValid || !confirmRecaptcha || isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {lang === 'en' ? 'Submitting...' : 'กำลังส่ง...'}
              </span>
            ) : lang === 'en' ? (
              'Submit'
            ) : (
              'ตกลง'
            )}
          </button>
        </div>
      </form>
      <SuccessRegistration
        showModal={showSuccess}
        setShowModal={setShowSuccess}
      />
    </div>
  );
};

ContactUs.getLayout = (page: ReactElement) => {
  return <OldLayout>{page}</OldLayout>;
};

export default ContactUs;
